
import React, { useState, useEffect } from 'react';
import { useUserDashboardData } from '@/hooks/useUserDashboardData';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface TimeItem {
  id: string;
  title: string;
  time: string;
  is_relative: boolean;
  relative_to?: string;
  offset_minutes?: number;
  show_time: boolean;
  user_id: string;
}

export const TimesManager: React.FC = () => {
  const { times, isLoading } = useUserDashboardData();
  const [localTimes, setLocalTimes] = useState<TimeItem[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { direction } = useLanguage();
  const isRtl = direction === 'rtl';

  // When remote times data changes, update local state
  useEffect(() => {
    if (times) {
      setLocalTimes(times);
    }
  }, [times]);

  const handleAddTime = () => {
    // Add a new empty time entry
    const newTime: TimeItem = {
      id: `temp-${Date.now()}`, // This will be replaced when saved
      title: '',
      time: '08:00',
      is_relative: false,
      show_time: true,
      user_id: ''
    };
    
    setLocalTimes([...localTimes, newTime]);
  };

  const handleTimeChange = (index: number, field: keyof TimeItem, value: any) => {
    const updatedTimes = [...localTimes];
    updatedTimes[index] = {
      ...updatedTimes[index],
      [field]: value
    };
    setLocalTimes(updatedTimes);
  };

  const handleRemoveTime = (index: number) => {
    const updatedTimes = [...localTimes];
    updatedTimes.splice(index, 1);
    setLocalTimes(updatedTimes);
  };

  const handleSaveTimes = async () => {
    setIsSubmitting(true);
    
    try {
      // For existing entries with an ID from DB, update them
      const updates = localTimes.filter(time => !time.id.startsWith('temp-')).map(time => 
        supabase
          .from('times')
          .update({
            title: time.title,
            time: time.time,
            is_relative: time.is_relative,
            relative_to: time.relative_to,
            offset_minutes: time.offset_minutes,
            show_time: time.show_time
          })
          .eq('id', time.id)
      );
      
      // For new entries (with temp IDs), insert them
      const newTimes = localTimes
        .filter(time => time.id.startsWith('temp-'))
        .map(time => ({
          title: time.title,
          time: time.time,
          is_relative: time.is_relative,
          relative_to: time.relative_to,
          offset_minutes: time.offset_minutes,
          show_time: time.show_time
          // No need to include user_id as it's added by Supabase RLS
        }));
      
      let insertError;
      if (newTimes.length > 0) {
        const { error } = await supabase.from('times').insert(newTimes);
        insertError = error;
      }
      
      // Execute all updates
      await Promise.all(updates);
      
      if (insertError) {
        throw insertError;
      }
      
      toast({
        title: isRtl ? 'הזמנים נשמרו בהצלחה' : 'Times saved successfully',
        description: isRtl ? 'כל הזמנים עודכנו בהצלחה' : 'All times have been updated successfully',
      });
      
    } catch (error) {
      console.error('Error saving times:', error);
      toast({
        variant: 'destructive',
        title: isRtl ? 'שגיאה בשמירת הזמנים' : 'Error saving times',
        description: (error as Error).message || isRtl ? 'אירעה שגיאה, אנא נסה שנית' : 'Something went wrong, please try again'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[300px]">
        <Loader2 className="h-8 w-8 animate-spin text-synagogue-blue" />
      </div>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className={isRtl ? 'text-right' : 'text-left'}>
          {isRtl ? 'ניהול זמני תפילה' : 'Manage Prayer Times'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {localTimes.map((timeItem, index) => (
            <div key={timeItem.id} className="p-4 border rounded-lg bg-gray-50">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-medium">{timeItem.title || `${isRtl ? 'זמן' : 'Time'} ${index + 1}`}</h3>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => handleRemoveTime(index)}
                >
                  <Trash2 className="h-5 w-5 text-red-500" />
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor={`title-${index}`}>{isRtl ? 'כותרת' : 'Title'}</Label>
                  <Input
                    id={`title-${index}`}
                    value={timeItem.title}
                    onChange={(e) => handleTimeChange(index, 'title', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor={`time-${index}`}>{isRtl ? 'זמן' : 'Time'}</Label>
                  <Input
                    id={`time-${index}`}
                    type="time"
                    value={timeItem.time}
                    onChange={(e) => handleTimeChange(index, 'time', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={timeItem.show_time}
                      onCheckedChange={(checked) => handleTimeChange(index, 'show_time', checked)}
                    />
                    <Label>{isRtl ? 'הצג זמן' : 'Show time'}</Label>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={timeItem.is_relative}
                      onCheckedChange={(checked) => handleTimeChange(index, 'is_relative', checked)}
                    />
                    <Label>{isRtl ? 'זמן יחסי' : 'Relative time'}</Label>
                  </div>
                </div>
                
                {timeItem.is_relative && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor={`relative-to-${index}`}>{isRtl ? 'יחסית ל-' : 'Relative to'}</Label>
                      <Select
                        value={timeItem.relative_to || ''}
                        onValueChange={(value) => handleTimeChange(index, 'relative_to', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={isRtl ? 'בחר זמן בסיס' : 'Select base time'} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sunrise">
                            {isRtl ? 'זריחה' : 'Sunrise'}
                          </SelectItem>
                          <SelectItem value="sunset">
                            {isRtl ? 'שקיעה' : 'Sunset'}
                          </SelectItem>
                          <SelectItem value="shabbat_start">
                            {isRtl ? 'כניסת שבת' : 'Shabbat Start'}
                          </SelectItem>
                          <SelectItem value="shabbat_end">
                            {isRtl ? 'יציאת שבת' : 'Shabbat End'}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor={`offset-${index}`}>{isRtl ? 'הסטה (דקות)' : 'Offset (minutes)'}</Label>
                      <Input
                        id={`offset-${index}`}
                        type="number"
                        value={timeItem.offset_minutes || 0}
                        onChange={(e) => handleTimeChange(index, 'offset_minutes', parseInt(e.target.value))}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
          
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={handleAddTime}
              className="flex items-center"
            >
              <Plus className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4`} />
              {isRtl ? 'הוסף זמן חדש' : 'Add New Time'}
            </Button>
            
            <Button 
              onClick={handleSaveTimes} 
              disabled={isSubmitting}
              className="flex items-center"
            >
              {isSubmitting && <Loader2 className={`${isRtl ? 'ml-1' : 'mr-1'} h-4 w-4 animate-spin`} />}
              {isRtl ? 'שמור זמנים' : 'Save Times'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
